import { useCallback, useEffect, useMemo, useState } from "react";
import BugReportIcon from "@mui/icons-material/BugReport";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ModalShell } from "./ModalShell";
import {
  clearApiDebugEvents,
  getApiDebugEvents,
  getDebugStatus,
  runDebugChecks,
} from "../api/palletApi";

function statusIcon(status) {
  if (status === "pass") {
    return <CheckCircleOutlineIcon color="success" />;
  }
  if (status === "skipped") {
    return <PauseCircleOutlineIcon color="warning" />;
  }
  return <ErrorOutlineIcon color="error" />;
}

export function DebugDiagnosticsModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [allowWrites, setAllowWrites] = useState(false);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [apiEvents, setApiEvents] = useState(() => getApiDebugEvents());

  const refreshApiEvents = useCallback(() => {
    setApiEvents(getApiDebugEvents());
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusError("");
    try {
      const payload = await getDebugStatus();
      setStatus(payload);
      setSelectedChecks((current) =>
        current.length ? current : payload.checks.map((check) => check.id)
      );
    } catch (error) {
      setStatus(null);
      setStatusError(error.message);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    window.addEventListener("palletPicker:api-debug-event", refreshApiEvents);
    return () =>
      window.removeEventListener("palletPicker:api-debug-event", refreshApiEvents);
  }, [refreshApiEvents]);

  const selectedCheckSet = useMemo(() => new Set(selectedChecks), [selectedChecks]);

  const toggleCheck = (checkId) => {
    setSelectedChecks((current) =>
      current.includes(checkId)
        ? current.filter((candidate) => candidate !== checkId)
        : [...current, checkId]
    );
  };

  const handleRunChecks = async () => {
    setRunning(true);
    setStatusError("");
    try {
      const payload = await runDebugChecks({
        checks: selectedChecks,
        allowWrites,
      });
      setResults(payload.checks || []);
    } catch (error) {
      setStatusError(error.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <ModalShell
      icon={<BugReportIcon />}
      title="Diagnostics"
      subtitle="Debug checks and recent API calls"
      onClose={onClose}
    >
      {statusError && (
        <div className="empty-state debug-error" role="alert">
          {statusError}
        </div>
      )}

      {status && (
        <>
          <div className="debug-toolbar">
            <button
              type="button"
              className="touch-button"
              onClick={handleRunChecks}
              disabled={running || selectedChecks.length === 0}
            >
              <PlayArrowIcon /> {running ? "Running..." : "Run Checks"}
            </button>
            <button type="button" className="icon-button" aria-label="Refresh diagnostics" onClick={loadStatus}>
              <RefreshIcon />
            </button>
            <label className="density-control">
              <input
                type="checkbox"
                checked={allowWrites}
                onChange={(event) => setAllowWrites(event.target.checked)}
                disabled={!status.allowWrites}
              />
              Allow write checks
            </label>
          </div>

          <div className="debug-meta">
            Backend writes: <strong>{status.allowWrites ? "enabled" : "disabled"}</strong>
            <span>Uptime: {status.uptimeSeconds}s</span>
          </div>

          <div className="debug-check-list" aria-label="Debug checks">
            {status.checks.map((check) => (
              <label key={check.id} className="debug-check-option">
                <input
                  type="checkbox"
                  checked={selectedCheckSet.has(check.id)}
                  onChange={() => toggleCheck(check.id)}
                />
                <span>
                  {check.label}
                  {check.write && " (write)"}
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      {results.length > 0 && (
        <section className="debug-section" aria-label="Debug check results">
          <h3>Check Results</h3>
          <div className="debug-result-list">
            {results.map((result) => (
              <div key={result.id} className={`debug-result ${result.status}`}>
                <div className="debug-result-title">
                  {statusIcon(result.status)}
                  <strong>{result.label}</strong>
                  <span>{result.durationMs}ms</span>
                </div>
                {result.reason && <p>{result.reason}</p>}
                {result.error && <p>{result.error}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="debug-section" aria-label="Recent API calls">
        <div className="debug-section-title">
          <h3>Recent API Calls</h3>
          <button
            type="button"
            className="icon-button"
            aria-label="Clear API debug events"
            onClick={() => {
              clearApiDebugEvents();
              refreshApiEvents();
            }}
          >
            <DeleteSweepIcon />
          </button>
        </div>

        {apiEvents.length === 0 ? (
          <div className="empty-state">No API calls recorded in this debug session.</div>
        ) : (
          <div className="debug-event-list">
            {apiEvents.slice(0, 12).map((event) => (
              <div key={event.id} className={`debug-event ${event.ok ? "pass" : "fail"}`}>
                <strong>
                  {event.method} {event.path}
                </strong>
                <span>
                  {event.status} · {event.durationMs}ms
                </span>
                {event.error && <span>{event.error}</span>}
              </div>
            ))}
          </div>
        )}
      </section>
    </ModalShell>
  );
}
